<template>
  <div class="quiz-type-answer-config">
    <div class="correct-type-answer">
      <label class="label-option">
        {{ $i18nPresenter.t('Correct answer to display') }}
        <span
          class="title-table-presentation-tooltip result-layout-tooltip"
        >
          <b-button
            v-tooltip="$i18nPresenter.t('This answer will be revealed when the time is up')"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </label>
      <div class="mb-2 input-group input-option">
        <InputText
          :val="$store.state.slideActive.correctQuizTypeAnswer"
          :id="null"
          :index="null"
          :place-holder="$i18nPresenter.t('Correct answer to display')"
          @interface="updateOption"
          :type="'input-option'"
          :id-name="'quizTypeAnswerCorrect'"
        />
      </div>
    </div>
    <div class="correct-type-answer" v-if="$store.state.slideActive.otherCorrectQuiz">
      <label class="label-option">
        {{ $i18nPresenter.t('Other accepted answers') }}
        <span
          class="title-table-presentation-tooltip result-layout-tooltip"
        >
          <b-button
            v-tooltip="$i18nPresenter.t('Answers matching any of these will also be marked as correct')"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </label>

      <draggable
        class="list-group"
        tag="ul"
        v-model="$store.state.slideActive.otherCorrectQuiz"
        v-bind="dragOptions"
        @start="drag = true"
        @end="drag = false"
        @change="sortOption()"
      >
        <li v-for="(item, index) in $store.state.slideActive.otherCorrectQuiz" :key="item.id">
          <div class="mb-2 input-group input-option">
            <div class="input-group-prepend handle-drag">
              <div class="input-group-text horizontal layout center center-justified">
                <i class="fas fa-arrows-alt-v" />
              </div>
            </div>
            <InputText
              :val="item.title"
              :id="'otherAnswerCorrectIndex' + index"
              :index="index"
              :place-holder="$i18nPresenter.t('otherAcceptedAnswerX', { number: (index + 1) })"
              @interface="updateOption"
              :type="'input-field'"
              :id-name="'otherAnswerCorrectIndex'"
            />
            <div
              class="input-group-prepend pointer"
            >
              <div
                class="input-group-text horizontal layout center center-justified"
                @click="deleteOption(index)"
              >
                <i class="far fa-trash-alt" />
              </div>
            </div>
          </div>
        </li>
      </draggable>

      <aha-button
        type="button"
        class="w-full"
        @click="addOption()"
        v-if="$store.state.slideActive.otherCorrectQuiz.length < 8"
      >
        <font-awesome-icon icon="plus" class="mr-1" /> {{ $i18nPresenter.t('Add') }}
      </aha-button>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { mapActions, mapState, mapGetters } from 'vuex'
import Util from '@/util'
import InputText from '@/components/presentations/InputText'
import Socket from '@/socket'
import EventBus from '@/eventBus'
import SlideApi from '@/api/slide'

export default {
  components: {
    InputText,
    draggable,
  },
  data() {
    return {
      drag: false,
    }
  },
  computed: {
    ...mapGetters(['isPresenting']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        delay: 0,
        ghostClass: 'ghost',
        handle: '.handle-drag',
      }
    },
    isTypeAnswerNoVoteCount() {
      const { typeAnswersData } = this.slideActive
      if (!typeAnswersData) return false
      const noVotes = typeAnswersData.every((answer) => !answer.count)
      const noLength = typeAnswersData && !typeAnswersData.length
      return noLength || noVotes
    },
  },
  created() {
    if (!this.$store.state.slideActive.otherCorrectQuiz) {
      this.updateSlideActiveAction({
        otherCorrectQuiz: [],
      })
    }
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),

    addOption() {
      if (this.$store.state.slideActive.otherCorrectQuiz.length > 7) return
      const { otherCorrectQuiz } = this.$store.state.slideActive
      otherCorrectQuiz.push({
        index: this.$store.state.slideActive.otherCorrectQuiz.length + 1,
        id: Math.random().toString(36).substring(7),
      })
      this.updateSlideActiveAction({
        otherCorrectQuiz,
      })
      Util.mixpanelTracking(
        'Slide',
        'Edit setting',
        'Presenter add other accepted answer',
      )

      EventBus.$emit('eventShowQuizTypeAnswer')
    },
    updateOption(item) {
      if (item.idName == 'otherAnswerCorrectIndex' && item.index !== undefined) {
        this._handleUpdateTitleOtherCorrectAnswer(item)
      } else if (item.idName == 'quizTypeAnswerCorrect') {
        this._handleUpdateTitleCorrectAnswer(item)
      }
    },

    async _handleUpdateTitleOtherCorrectAnswer(item) {
      const { otherCorrectQuiz } = this.slideActive
      if (!otherCorrectQuiz) return
      otherCorrectQuiz[item.index].title = item.value
      const payload = {
        otherCorrectQuiz,
      }
      this.updateSlideActiveAction(payload)

      if (this.isTypeAnswerNoVoteCount) {
        payload.otherCorrectQuiz = JSON.stringify(payload.otherCorrectQuiz)
        this._updateAnswerNoVoteCount(payload)
      } else {
        await this._updateSlide()
        EventBus.$emit('eventShowQuizTypeAnswer')
      }
      Util.mixpanelTracking(
        'Slide',
        'Edit setting',
        'Presenter update other accepted answer',
      )
    },

    async _handleUpdateTitleCorrectAnswer(item) {
      if (this.isTypeAnswerNoVoteCount) {
        this._updateCorrectAnswerNoVoteCount(item)
      } else {
        this.updateSlideActiveAction({
          correctQuizTypeAnswer: item.value,
        })
        await this._updateSlide()
        EventBus.$emit('eventShowQuizTypeAnswer')
      }
      Util.mixpanelTracking(
        'Slide',
        'Edit setting',
        'Presenter update correct answer',
      )
    },

    async _updateAnswerNoVoteCount(payload) {
      EventBus.$emit('type-answer-no-vote: update-title')
      await this._handleUpdateSlide(payload)
      EventBus.$emit('type-answer-no-vote: update-title-done')
    },

    async _updateCorrectAnswerNoVoteCount(item) {
      this.slideActive.typeAnswersData[0] = {
        ...this.slideActive.typeAnswersData[0],
        alias: item.value,
        title: item.value,
      }
      const payload = {
        correctQuizTypeAnswer: item.value,
        typeAnswersData: this.slideActive.typeAnswersData,
      }
      this.updateSlideActiveAction(payload)
      this._updateAnswerNoVoteCount(payload)
    },

    async _handleUpdateSlide(dataUpdate) {
      try {
        const payload = {
          ...dataUpdate,
          socketId: Socket.getSocketId(),
          accessCode: this.$store.state.presentation.uniqueAccessCode,
          presenting: this.isPresenting,
        }
        this.setSyncLoadingAction(1)
        await SlideApi.updateSlideById(this.slideActive.id, payload)
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
      }
    },
    deleteOption(index) {
      const { otherCorrectQuiz } = this.$store.state.slideActive
      otherCorrectQuiz.splice(index, 1)
      otherCorrectQuiz.forEach((item, key) => {
        item.index = key + 1
      })
      this.updateSlideActiveAction({
        otherCorrectQuiz,
      })
      this._updateSlide()
      Util.mixpanelTracking(
        'Slide',
        'Edit setting',
        'Presenter delete other accepted answer',
      )
      EventBus.$emit('eventShowQuizTypeAnswer')
    },
    sortOption() {
      this._updateSlide()
      Util.mixpanelTracking(
        'Slide',
        'Edit setting',
        'Presenter sort other accepted answer',
      )
      EventBus.$emit('eventShowQuizTypeAnswer')
    },

    async _updateSlide() {
      try {
        this.setSyncLoadingAction(1)
        const payload = {
          otherCorrectQuiz: JSON.stringify(
            this.slideActive.otherCorrectQuiz,
          ),
          correctQuizTypeAnswer: this.slideActive.correctQuizTypeAnswer,
          socketId: Socket.getSocketId(),
          accessCode: this.presentation.uniqueAccessCode,
          presenting: this.isPresenting,
        }
        await SlideApi.updateSlideById(this.slideActive.id, payload)
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
      }
    },

  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.correct-type-answer {
  margin-bottom: 16px;

  & ::v-deep .input-style.error {
    input {
      border: 1px solid #ddd;
      color: inherit;
    }
    .value-validate-length {
      color: $nobel-color;
    }
  }
}
.pick-option-point {
  padding-left: 23px;
  .form-check-inline {
    margin: 12px 0;
  }
}
.item-qna {
  cursor: pointer;

  label {
    cursor: pointer;
  }

  input {
    margin-right: 4px;
    position: relative;
    top: -1px;
    cursor: pointer;
  }
}
.qna-block {
  .block-content {
    margin-bottom: 16px;
    .form-check-inline {
      cursor: pointer;

      input,
      label {
        cursor: pointer;
      }
    }
  }

  .block-body {
    margin-bottom: 8px !important;
  }
}
.option-img-btn {
  position: relative;

  .question-img-tooltip {
    z-index: 9;
  }
}
.input-group {
  user-select: none;
}

.wordcloud-input {
  input {
    width: 70px;
  }
}
.button-show-all {
  position: absolute;
  max-width: 250px;
  margin-left: 8px;
  margin-top: -4px;
  border: none;
  line-height: 29px;
  font-size: 14px;
}
.label-time-to-answer {
  position: absolute;
  top: 43px;
  left: 90px;
}

.question-img-btn {
  .question-img-btn-box {
    padding: 14px 12px;
    height: 44px;
    border-radius: 0;
    .fa-image {
      font-size: 17px;
    }
    img {
      width: 200%;
      max-height: 30px;
    }
    .fa-pencil-alt {
      display: none;
    }
  }
  .btn-box-edit:hover {
    img {
      display: none;
    }
    .fa-pencil-alt {
      display: block;
    }
  }
}
.question-img-tooltip {
  position: absolute;
  right: 55px;
  top: -17px;
  .tooltip-box {
    background-color: rgba(0, 0, 0, 0.9);
    height: 84px;
    width: 84px;
    border-radius: 5px;
    .btn {
      padding: 0.1rem 0.55rem;
      font-weight: bold;
    }
  }
  .tooltip-arrow {
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 6px solid rgba(0, 0, 0, 0.9);
    position: absolute;
    left: 84px;
    top: 40%;
  }
}

.form-check-inline {
  display: block;
  margin-bottom: 12px;
  &.form-check-font-scpro {
    margin-bottom: 40px;
  }
}

.custom-control-label {
  cursor: pointer;
}

.label-option-small {
  font-size: 14px !important;
  color: #313131 !important;
  padding-left: 10px;
  padding-left: 23px;
}

.range-limit-choice {
  position: relative;
  padding-left: 23px;
  .number {
    font-weight: bold;
    color: #666;
    font-size: 14px;
  }

  input {
    margin: 0 16px;
  }

  .value {
    position: absolute;
    top: -16px;
    left: 50%;
    font-size: 16px;
    font-weight: bold;
    color: #fff;
    width: 30px;
    height: 30px;
    background-color: $primary-color;
    border-radius: 50%;
    text-align: center;
    line-height: 30px;
    margin-left: -15px;
  }
}
.btn-block.button-add-option {
  margin-bottom: 16px;
}

.input-option {
  & ::v-deep .input-style {
    width: min-content;
  }
}

ul {
  li {
    list-style: none;
  }
}

.disabled {
  opacity: 0.6;
  pointer-events: none;
}
.not-allowed {
  cursor: not-allowed;
}

.check-correct-answer {
  width: 44px;
  height: 44px;
  margin-left: 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
  cursor: pointer;
  i {
    display: none;
    color: #fff;
  }

  &.correct {
    background-color: $secondary-color;
    border-color: $secondary-color;

    i {
      display: block;
    }
  }
}

.hide-option {
  display: none;
}

.title-desc {
  .title-desc-link {
    font-size: 0.875em;
  }
  .title-desc-edit-wrapper {
    .title-desc-info {
      opacity: 0.7;
      font-size: 0.875em;
    }
  }
}
</style>
